/**
 * Stored-data check ("peek") — a read-only diagnostic export (Pedro 2026-07-12). Over a month
 * range it downloads a per-account snapshot of what is ON RECORD: sheet 1 the stored 20th-of-month
 * AuM (fee basis), sheet 2 the frozen fee (net, ex-VAT) — one row per account (agreement · bank ·
 * number), one column per month. Lets an operator verify exactly what per-account data is stored
 * and spot a wrong upload (the detection half of the "guard prevents / overwrite corrects" model).
 * Lives under Fees → Setup. Admin-only (the endpoint gates).
 */
import { useState } from 'react';
import { Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconDownload } from '@tabler/icons-react';

import { accountMetricsApi } from '../api/accountMetrics';

const ym = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : '';

export function StoredDataPeekSection() {
  // Default the window to the Dec-2025 anchor onward (the earliest per-account upload month).
  const [from, setFrom] = useState<Date | null>(new Date('2025-12-01T00:00:00'));
  const [to, setTo] = useState<Date | null>(null);
  const [downloading, setDownloading] = useState(false);

  async function handle() {
    if (!from || !to) return;
    setDownloading(true);
    try {
      await accountMetricsApi.downloadPeek(ym(from), ym(to));
    } catch (e) {
      notifications.show({
        color: 'red', title: 'Peek export failed',
        message: e instanceof Error ? e.message : 'Could not export.',
      });
    } finally { setDownloading(false); }
  }

  return (
    <Card withBorder padding="lg">
      <Stack>
        <div>
          <Title order={4}>Fee calc data check</Title>
          <Text c="dimmed" size="sm">
            Download a read-only per-account snapshot over a month range — the stored
            20th-of-month AuM (fee basis), the frozen fee and the annual fee %, one row per
            account (agreement · bank · number) and one column per month — plus a Fee calculation
            sheet: for each frozen fee, the months whose 20th-of-month AuM were averaged, the
            average used, the rate and the fee, with a check that the stored months still match.
            Use it to verify exactly what is on record and to spot a wrong upload.
          </Text>
        </div>
        <Group align="flex-end">
          <MonthPickerInput label="From" placeholder="First month" value={from} onChange={setFrom} maw={200} />
          <MonthPickerInput label="To" placeholder="Last month" value={to} onChange={setTo} maw={200} />
          <Button leftSection={<IconDownload size={16} />} onClick={handle} loading={downloading} disabled={!from || !to}>
            Download peek
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
